import { ThemeEvents } from '@theme/events';

class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
    this.#applyInitialFilter();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
  }

  updateMediaGallery = (event) => {
    const newMediaGallery = event.detail.data.html.querySelector('product-media-gallery slider-component');
    const currentMediaGallery = this.querySelector('slider-component');

    if (!newMediaGallery || !currentMediaGallery) return;

    const variantTokens = this.#getVariantFilterTokens(event);
    if (variantTokens.length > 0) {
      this.#filterMediaByAlt(newMediaGallery, variantTokens);
    }

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      currentMediaGallery.replaceWith(newMediaGallery);
    }
  };

  #getVariantFilterTokens(event) {
    const variant = event?.detail?.resource;
    if (variant?.title) {
      return this.#normalizeTokens(variant.title.split(' / '));
    }

    const variantFromPicker = this.#getSelectedVariantFromPicker();
    if (variantFromPicker?.title) {
      return this.#normalizeTokens(variantFromPicker.title.split(' / '));
    }

    return this.#normalizeTokens(this.#getSelectedOptionValues());
  }

  #getSelectedVariantFromPicker() {
    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    const json = variantPicker?.querySelector('script[type="application/json"]')?.textContent;
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  #getSelectedOptionValues() {
    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    if (!variantPicker) return [];

    const values = [];

    variantPicker.querySelectorAll('select').forEach((select) => {
      const selectedOption = select.querySelector('option[selected]') || select.options[select.selectedIndex];
      if (selectedOption?.value) {
        values.push(selectedOption.value);
      }
    });

    variantPicker.querySelectorAll('fieldset input:checked').forEach((input) => {
      if (input.value) {
        values.push(input.value);
      }
    });

    return values;
  }

  #normalizeTokens(tokens) {
    return [...new Set(tokens.map((token) => this.#normalize(token)).filter(Boolean))];
  }

  #normalize(value) {
    return `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\+/g, 'plus');
  }

  #filterMediaByAlt(sliderComponent, variantTokens) {
    const mainItems = Array.from(sliderComponent.querySelectorAll('.product-media-gallery__media'));
    const thumbnailItems = Array.from(sliderComponent.querySelectorAll('.product-media-gallery__thumbnail'));

    const hasMainMatches = this.#filterNodesByAlt(mainItems, variantTokens);
    const hasThumbnailMatches = this.#filterNodesByAlt(thumbnailItems, variantTokens);

    if (!hasMainMatches && !hasThumbnailMatches) return;

    if (sliderComponent.tagName === 'SLIDER-COMPONENT') {
      sliderComponent.slider?.refresh?.();
      sliderComponent.thumbnailSlider?.refresh?.();
      sliderComponent.slider?.go?.(0);
    }
  }

  #applyInitialFilter() {
    const sliderComponent = this.querySelector('slider-component');
    if (!sliderComponent) return;

    const variantTokens = this.#getVariantFilterTokens();
    if (variantTokens.length === 0) return;

    this.#filterMediaByAlt(sliderComponent, variantTokens);
  }

  #parseAltFilters(alt) {
    const orOptions = [];
    let rest = alt;

    const orGroupMatch = alt.match(/\(([^)]+)\)/);
    if (orGroupMatch) {
      orOptions.push(...orGroupMatch[1].split('|').map((option) => this.#normalize(option)).filter(Boolean));
      rest = alt.replace(orGroupMatch[0], ' ').replace(/\s+/g, ' ').trim();
    }

    return { orOptions, rest };
  }

  #tokenIsMatchedByAlt(token, rest, orOptions) {
    if (rest.includes(token)) return true;
    if (orOptions.length === 0) return false;

    return orOptions.some((option) => option.includes(token) || token.includes(option));
  }

  #altMatchesTokens(alt, variantTokens) {
    if (variantTokens.length === 0) return true;
    if (alt === 'all') return true;

    if (/\ball\b/.test(alt)) {
      const constraint = alt.replace(/\ball\b/g, '').replace(/\s+/g, ' ').trim();
      if (!constraint) return true;

      return variantTokens.some((token) => constraint.includes(token) || token.includes(constraint));
    }

    const { orOptions, rest } = this.#parseAltFilters(alt);

    return variantTokens.every((token) => this.#tokenIsMatchedByAlt(token, rest, orOptions));
  }

  #filterNodesByAlt(nodes, variantTokens) {
    if (nodes.length === 0) return false;

    const matches = new Set();
    for (const node of nodes) {
      const image = node.querySelector('img');
      if (!image) {
        matches.add(node);
        continue;
      }

      const alt = this.#normalize(image.alt);
      if (this.#altMatchesTokens(alt, variantTokens)) {
        matches.add(node);
      }
    }

    if (matches.size === 0) return false;

    for (const node of nodes) {
      if (!matches.has(node)) {
        node.remove();
      }
    }

    return true;
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
